import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import { KyndrylCatalogNode } from './KyndrylCatalogNode';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';


import {
  EntityArgoCDOverviewCard,
  EntityArgoCDHistoryCard,
  isArgocdAvailable,
} from '@roadiehq/backstage-plugin-argo-cd';

import {
  HumanitecCardComponent,
} from '@humanitec/backstage-plugin';

import { GiteaActionsContent, isGiteaActionsAvailable } from '../cicd/GiteaActionsContent';
import { GiteaPromoteContent, isGiteaPromoteAvailable } from '../gitea/GiteaPromoteContent';
import { HumanitecV2Content, isHumanitecV2Available } from '../humanitec/HumanitecV2Content';
import { Day2ActionsCard, Day2RollbackCard, isDay2ActionsAvailable } from '../day2/Day2ActionsCard';
import { K8sOperationsContent, isK8sOperationsAvailable } from '../k8s/K8sOperationsContent';
import { ArgoCDPromotionContent, isArgoCDPromotionAvailable } from '../argocd-promotion/ArgoCDPromotionContent';

import { CopilotContent } from '../copilot';

import {
  isAlertSelectorAvailable,
} from '@backstage-community/plugin-grafana';

import { EntitySonarQubeCard, SonarQubeRelatedEntitiesOverview } from '@backstage-community/plugin-sonarqube';
import { isSonarQubeAvailable } from '@backstage-community/plugin-sonarqube-react';

import {
  GithubIssuesCard,
  GithubIssuesPage,
} from '@backstage-community/plugin-github-issues';

import { ObservabilityTabContent } from '@internal/plugin-grafana-dashboard';
import { SonarQubeTrendCard } from './SonarQubeTrendCard';
import { HelmChartParametersContent, isHelmChartWithParameters } from './HelmChartParametersContent';

// v1 Humanitec plugin: only show for components with humanitec.com/appId but NOT the v2 annotations
const isHumanitecAvailable = (entity: any) =>
  Boolean(entity?.metadata?.annotations?.['humanitec.com/appId']) &&
  !entity?.metadata?.annotations?.['humanitec.dev/projectId'];

const isGithubIssuesAvailable = (entity: any) =>
  Boolean(entity?.metadata?.annotations?.['github.com/project-slug']);

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isGiteaActionsAvailable}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <GiteaActionsContent />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isGiteaPromoteAvailable}>
            <Grid item xs={12} md={6}>
              <GiteaPromoteContent />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={isSonarQubeAvailable}>
            <Grid item xs={12} md={6}>
              <EntitySonarQubeCard variant="gridItem" />
            </Grid>
            <Grid item xs={12} md={6}>
              <SonarQubeTrendCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="Add gitea.kyndemo.live/repo-slug: <owner>/<repo> to enable Gitea Actions CI/CD for it."
        action={
          <Button
            variant="contained"
            color="primary"
            href="/docs"
          >
            Read More
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard variant="gridItem" />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} renderNode={KyndrylCatalogNode} />
    </Grid>
    <EntitySwitch>
      <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
        <Grid item xs={12} md={6}>
          <EntityArgoCDOverviewCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityArgoCDHistoryCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={isGithubIssuesAvailable}>
        <Grid item xs={12} md={6}>
          <GithubIssuesCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <Grid item md={4} xs={12}>
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <EntityLinksCard />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={e => isDay2ActionsAvailable(e)}>
            <Grid item>
              <Day2ActionsCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/argocd" title="Argo CD" if={isArgocdAvailable}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={12}>
          <EntityArgoCDOverviewCard />
        </Grid>
        <Grid item md={12}>
          <EntityArgoCDHistoryCard />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isArgoCDPromotionAvailable}>
            <Grid item md={12}>
              <ArgoCDPromotionContent />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/humanitec" title="Humanitec" if={isHumanitecAvailable}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={12}>
          <HumanitecCardComponent />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/platform" title="Orchestrator" if={isHumanitecV2Available}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HumanitecV2Content />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isK8sOperationsAvailable}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <K8sOperationsContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/observability" title="Observability" if={isAlertSelectorAvailable}>
      <ObservabilityTabContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/github-issues" title="Issues" if={isGithubIssuesAvailable}>
      <GithubIssuesPage />
    </EntityLayout.Route>

    <EntityLayout.Route path="/copilot" title="Copilot">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CopilotContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/argocd" title="Argo CD" if={isArgocdAvailable}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={12}>
          <EntityArgoCDOverviewCard />
        </Grid>
        <Grid item md={12}>
          <EntityArgoCDHistoryCard />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isArgoCDPromotionAvailable}>
            <Grid item md={12}>
              <ArgoCDPromotionContent />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/humanitec" title="Humanitec" if={isHumanitecAvailable}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={12}>
          <HumanitecCardComponent />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/platform" title="Platform (v2)" if={isHumanitecV2Available}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HumanitecV2Content />
        </Grid>
        <Grid item xs={12} md={6}>
          <Day2RollbackCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isK8sOperationsAvailable}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <K8sOperationsContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/github-issues" title="Issues" if={isGithubIssuesAvailable}>
      <GithubIssuesPage />
    </EntityLayout.Route>

    <EntityLayout.Route path="/copilot" title="Copilot">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CopilotContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} renderNode={KyndrylCatalogNode} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/copilot" title="Copilot">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CopilotContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/copilot" title="Copilot">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CopilotContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} renderNode={KyndrylCatalogNode} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
        renderNode={KyndrylCatalogNode}
      />
    </EntityLayout.Route>
    <EntityLayout.Route path="/observability" title="Observability" if={isAlertSelectorAvailable}>
      <ObservabilityTabContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/argocd" title="Argo CD" if={isArgocdAvailable}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={12}>
          <EntityArgoCDOverviewCard />
        </Grid>
        <Grid item md={12}>
          <EntityArgoCDHistoryCard />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isArgoCDPromotionAvailable}>
            <Grid item md={12}>
              <ArgoCDPromotionContent />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/sonarqube" title="Code Quality">
      <SonarQubeRelatedEntitiesOverview relationType={RELATION_HAS_PART} entityKind="component" />
    </EntityLayout.Route>

    <EntityLayout.Route path="/copilot" title="Copilot">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CopilotContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} renderNode={KyndrylCatalogNode} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/copilot" title="Copilot">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CopilotContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// ── Agent Entity Page ───────────────────────────────────────────────────
const agentEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} renderNode={KyndrylCatalogNode} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/observability" title="Observability">
      <ObservabilityTabContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/copilot" title="Copilot">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CopilotContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// ── Resource Entity Page ─────────────────────────────────────────────────
const resourceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/parameters" title="Parameters" if={isHelmChartWithParameters}>
      <HelmChartParametersContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/copilot" title="Copilot">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CopilotContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// ── MCPServer Entity Page ───────────────────────────────────────────────
const mcpServerEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} renderNode={KyndrylCatalogNode} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/copilot" title="Copilot">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <CopilotContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('agent')} children={agentEntityPage} />
    <EntitySwitch.Case if={isKind('mcpserver')} children={mcpServerEntityPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourceEntityPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
